import { cx } from "@emotion/css";
import Avatar from "@mui/material/Avatar";
import { MoreVertical } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PrimaryButton } from "src/components/Default/LekUI/Button";
import { Menu } from "src/components/Default/LekUI/Menu";
import { FolderSelectionCheckbox } from "src/components/Folders/FolderSelectionCheckbox";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { Badge } from "src/components/NewComponents/Default/Badge";
import type { SearchDeck } from "src/hooks/useFlashcards";
import AdminProfileIcon from "src/images/Icons/newIcons/userProfile/logo.svg";

interface MenuItem {
	title: string;
	onClick?: (event?: React.MouseEvent) => void;
	Icon?: ReactNode;
	testId?: string;
	itemTextClassName?: string;
	className?: string;
	items?: MenuItem[];
}

interface MenuDivider {
	isDivider: true;
}

type DeckMenuEntry = MenuItem | MenuDivider;

interface DeckCardPrimaryAction {
	label: string;
	to: string;
	testId?: string;
}

interface DeckCardProps {
	deck: SearchDeck;
	menuItems?: DeckMenuEntry[];
	badges?: ReactNode[];
	className?: string;
	hidePreview?: boolean;
	primaryAction?: DeckCardPrimaryAction;
	showUsedCount?: boolean;
	isSelectMode?: boolean;
	isSelected?: boolean;
	onToggleSelect?: () => void;
	onToggleList?: () => void;
}

export function DeckCard({
	deck,
	menuItems,
	badges,
	className,
	hidePreview,
	primaryAction,
	showUsedCount,
	isSelectMode,
	isSelected,
	onToggleSelect,
	onToggleList,
}: DeckCardProps) {
	const { t } = useTranslation();
	const { openModal } = useModals();

	return (
		<div
			className={cx(
				"deck-search-card",
				isSelectMode && "cursor-pointer selectable-hover",
				isSelected && "border-color-primary",
				className,
			)}
			data-test-id="deck-card"
			onClick={isSelectMode ? onToggleSelect : undefined}
		>
			{isSelectMode && <FolderSelectionCheckbox isSelected={!!isSelected} />}
			<div className="flex flex-col gap-8 mb-24">
				<div className="flex justify-between items-center gap-8">
					<div className="flex gap-8 flex-wrap">
						<Badge>
							{t("decks.search.flashcardsCount", "{{count}} pojęć", {
								count: deck.flashcardsCount,
							})}
						</Badge>
						{showUsedCount && (
							<Badge>
								{t("decks.search.usedCount", "Rozwiązano {{count}} razy", {
									count: deck.usedCount,
								})}
							</Badge>
						)}
						{badges}
					</div>
					{menuItems && (
						<Menu
							icon={<MoreVertical className="icon-small" />}
							anchorId="deck-card-menu"
							closeAfterClick
							items={menuItems}
						/>
					)}
				</div>
				<div className="flex flex-col gap-4">
					<span className="text-ellipsis-1 size-16">{deck.name}</span>
					{deck.description && (
						<p className="deck-search-card--description text-ellipsis-2 m-0 size-16-light text-black-80">
							{deck.description}
						</p>
					)}
				</div>
			</div>

			<div className="flex justify-between items-center">
				<div className="flex items-center gap-8">
					<Avatar
						src={deck.user?.avatarUrl || undefined}
						className="avatar-28 avatar-square"
					/>
					<span className="size-14 text-grey text-ellipsis-1">
						{deck.user?.username}
					</span>
					{deck.isCompanyDeck && !deck.category && (
						<div className="deck-search-card--admin-icon">
							<AdminProfileIcon />
						</div>
					)}
				</div>

				{primaryAction ? (
					<PrimaryButton
						className="deck-search-card--preview-btn selectable-hover-skip"
						component={Link}
						to={primaryAction.to}
						onClick={(e: React.MouseEvent) => {
							e.stopPropagation();
						}}
						data-test-id={primaryAction.testId}
					>
						{primaryAction.label}
					</PrimaryButton>
				) : !hidePreview ? (
					<PrimaryButton
						className="deck-search-card--preview-btn selectable-hover-skip"
						onClick={(e: React.MouseEvent) => {
							e.stopPropagation();
							openModal(ModalsNames.DeckPreviewModal, {
								deckId: deck.id,
								deckName: deck.name,
								isInList: !!isSelected,
								onToggleList,
							});
						}}
					>
						{t("decks.search.preview", "Podgląd")}
					</PrimaryButton>
				) : null}
			</div>
		</div>
	);
}
